/**
 * Sender Keys Protocol Implementation
 * Used for efficient group message encryption
 * Based on Signal Protocol's Sender Keys specification
 */

import nacl from 'tweetnacl';
import * as naclUtil from 'tweetnacl-util';

export interface SenderKey {
  key: Uint8Array; // 32-byte symmetric key
  keyId: number; // Unique key ID
  createdAt: number; // Timestamp
  messageCount: number; // Number of messages encrypted with this key
}

export interface EncryptedSenderKeyMessage {
  keyId: number;
  messageNumber: number;
  ciphertext: string; // Base64 encoded encrypted message
  nonce: string; // Base64 encoded nonce
}

/**
 * Generate a new sender key
 */
export function generateSenderKey(): SenderKey {
  return {
    key: nacl.randomBytes(32),
    keyId: Date.now(), // Simple key ID generation (could use UUID)
    createdAt: Date.now(),
    messageCount: 0
  };
}

/**
 * Encrypt message with sender key
 */
export function encryptWithSenderKey(
  senderKey: SenderKey,
  plaintext: string
): { updatedKey: SenderKey; message: EncryptedSenderKeyMessage } {
  // Generate nonce (24 bytes for secretbox)
  const nonce = nacl.randomBytes(24);
  
  // Encrypt message
  const plaintextBytes = naclUtil.decodeUTF8(plaintext);
  const ciphertext = nacl.secretbox(plaintextBytes, nonce, senderKey.key);
  
  if (!ciphertext) {
    throw new Error('Encryption failed');
  }
  
  // Update sender key
  const updatedKey: SenderKey = {
    ...senderKey,
    messageCount: senderKey.messageCount + 1
  };
  
  return {
    updatedKey,
    message: {
      keyId: senderKey.keyId,
      messageNumber: senderKey.messageCount,
      ciphertext: naclUtil.encodeBase64(ciphertext),
      nonce: naclUtil.encodeBase64(nonce)
    }
  };
}

/**
 * Decrypt message with sender key
 */
export function decryptWithSenderKey(
  senderKey: SenderKey,
  message: EncryptedSenderKeyMessage
): string {
  // Verify key ID matches
  if (message.keyId !== senderKey.keyId) {
    throw new Error('Sender key ID mismatch');
  }
  
  // Decrypt message
  const ciphertext = naclUtil.decodeBase64(message.ciphertext);
  const nonce = naclUtil.decodeBase64(message.nonce);
  const plaintextBytes = nacl.secretbox.open(ciphertext, nonce, senderKey.key);
  
  if (!plaintextBytes) {
    throw new Error('Decryption failed - message may be corrupted');
  }
  
  return naclUtil.encodeUTF8(plaintextBytes);
}

/**
 * Encrypt sender key for a recipient
 * Uses recipient's public key to encrypt the sender key
 */
export function encryptSenderKeyForRecipient(
  senderKey: SenderKey,
  recipientPublicKey: Uint8Array,
  senderSecretKey: Uint8Array
): string {
  // Serialize sender key
  const keyData = JSON.stringify({
    key: naclUtil.encodeBase64(senderKey.key),
    keyId: senderKey.keyId,
    createdAt: senderKey.createdAt
  });
  
  // Encrypt with recipient's public key
  const nonce = nacl.randomBytes(24);
  const keyBytes = naclUtil.decodeUTF8(keyData);
  const encrypted = nacl.box(keyBytes, nonce, recipientPublicKey, senderSecretKey);
  
  if (!encrypted) {
    throw new Error('Failed to encrypt sender key');
  }
  
  // Include nonce in the blob so it can be used for decryption
  // Format: base64(nonce) + ":" + base64(encrypted)
  const nonceBase64 = naclUtil.encodeBase64(nonce);
  const encryptedBase64 = naclUtil.encodeBase64(encrypted);
  return `${nonceBase64}:${encryptedBase64}`;
}

/**
 * Decrypt sender key from encrypted blob
 */
export function decryptSenderKeyFromBlob(
  encryptedBlob: string,
  recipientSecretKey: Uint8Array,
  senderPublicKey: Uint8Array
): SenderKey {
  try {
    // Parse the blob - format: base64(nonce):base64(encrypted)
    // Handle both old format (no nonce) and new format (with nonce)
    let nonce: Uint8Array;
    let encrypted: Uint8Array;
    
    if (encryptedBlob.includes(':')) {
      // New format: nonce:encrypted
      const parts = encryptedBlob.split(':');
      if (parts.length !== 2) {
        throw new Error('Invalid encrypted blob format');
      }
      nonce = naclUtil.decodeBase64(parts[0]);
      encrypted = naclUtil.decodeBase64(parts[1]);
    } else {
      // Old format: just encrypted (for backward compatibility)
      // Try with zero nonce first (legacy)
      encrypted = naclUtil.decodeBase64(encryptedBlob);
      nonce = new Uint8Array(24); // Zero nonce
    }
    
    // Validate nonce length
    if (nonce.length !== 24) {
      throw new Error(`Invalid nonce length: ${nonce.length}, expected 24`);
    }
    
    // Validate encrypted data length
    if (encrypted.length === 0) {
      throw new Error('Encrypted data is empty');
    }
    
    let decrypted = nacl.box.open(encrypted, nonce, senderPublicKey, recipientSecretKey);
    
    if (!decrypted) {
      // If old format failed with zero nonce, the message might have been encrypted with a random nonce
      // but stored without it. Unfortunately, we can't decrypt these messages.
      // This happens when:
      // 1. Message was encrypted before the fix (random nonce used but not stored)
      // 2. The nonce was lost or corrupted
      // 3. Wrong keys are being used
      
      // For old format messages, we've already tried with zero nonce and failed
      // There's no way to recover the original nonce, so we must fail
      throw new Error('Failed to decrypt sender key - message may have been encrypted with a different format or the nonce was lost');
    }
    
    // Deserialize sender key
    const keyData = JSON.parse(naclUtil.encodeUTF8(decrypted));
    
    // Validate key data structure
    if (!keyData.key || !keyData.keyId) {
      throw new Error('Invalid sender key data structure');
    }
    
    return {
      key: naclUtil.decodeBase64(keyData.key),
      keyId: keyData.keyId,
      createdAt: keyData.createdAt || Date.now(),
      messageCount: 0 // Reset for recipient
    };
  } catch (error: any) {
    // Provide more context about the error
    const errorMessage = error?.message || String(error);
    throw new Error(`Failed to decrypt sender key: ${errorMessage}`);
  }
}

/**
 * Serialize sender key for storage
 */
export function serializeSenderKey(key: SenderKey): string {
  return JSON.stringify({
    key: naclUtil.encodeBase64(key.key),
    keyId: key.keyId,
    createdAt: key.createdAt,
    messageCount: key.messageCount
  });
}

/**
 * Deserialize sender key from storage
 */
export function deserializeSenderKey(serialized: string): SenderKey {
  const data = JSON.parse(serialized);
  return {
    key: naclUtil.decodeBase64(data.key),
    keyId: data.keyId,
    createdAt: data.createdAt,
    messageCount: data.messageCount || 0
  };
}

